import {
  Box,
  Button,
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  DrawerTrigger,
  Icon,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  ScrollArea,
  Separator,
  Text,
  useIsMobile,
} from "@salestrak/ui";
import * as React from "react";

import { CustomizeNavigationDialog } from "./customize-navigation-dialog";
import { useModuleRail } from "./module-rail-context";
import { RailTile } from "./rail-tile";
import type { ResolvedRailTile } from "#app/lib/hooks/use-nav-preferences";
import type { Omittable } from "@salestrak/ui";

/**
 * MoreModulesTile — the fixed "More" rail tile. Opens an overlay listing the
 * overflow modules (tiles the user hid from the rail) plus a Customize
 * Navigation action: a popover anchored to the tile on desktop, a drawer on
 * mobile. The overflow list and its press handler come from `ModuleRail`,
 * which owns route resolution (e.g. Workflows → first board) and the
 * drawer-keep-open semantics shared with the visible tiles.
 */
export function MoreModulesTile({
  overflowTiles,
  onTilePress,
  onInvitePress,
  popoverTopInset = 0,
}: Omittable<{
  overflowTiles: ResolvedRailTile[];
  onTilePress: (tile: ResolvedRailTile) => void;
  /** When set, an Invite row renders after the overflow tiles — the rail
   * passes it when its column is too short to fit the pinned Invite tile. */
  onInvitePress?: () => void;
  /** Window-Y of the rail's top edge. Fed to the popover as a top collision
   * inset so collision-avoidance never shifts the popup above the rail. */
  popoverTopInset?: number;
}>) {
  const isMobile = useIsMobile();
  const { compact } = useModuleRail();
  const [open, setOpen] = React.useState(false);
  const [customizeOpen, setCustomizeOpen] = React.useState(false);

  const handleCustomize = () => {
    // Swap the popover/drawer for the customize dialog.
    setOpen(false);
    setCustomizeOpen(true);
  };

  const handleTilePress = (tile: ResolvedRailTile) => {
    setOpen(false);
    onTilePress(tile);
  };

  const tile = <RailTile compact={compact} icon="grid-dots" label="More" />;

  const rows = (
    <Box direction="column" gap="sm">
      {overflowTiles.length === 0 && onInvitePress == null ? (
        <Text size="xs" variant="muted">
          No additional modules yet.
        </Text>
      ) : (
        overflowTiles.map((overflowTile, index) => (
          <Button
            className="w-full justify-start"
            key={`${overflowTile.id}-${index}`}
            onClick={() => handleTilePress(overflowTile)}
            variant="ghost"
          >
            <Icon name={overflowTile.icon} size="sm" />
            <Text>{overflowTile.label}</Text>
          </Button>
        ))
      )}
      {onInvitePress != null && (
        <Button
          className="w-full justify-start"
          onClick={() => {
            setOpen(false);
            onInvitePress();
          }}
          variant="ghost"
        >
          <Icon name="user-plus" size="sm" />
          <Text>Invite</Text>
        </Button>
      )}
    </Box>
  );

  const customizeButton = (
    <Button className="w-full" onClick={handleCustomize} variant="outline">
      <Icon name="settings" size="sm" />
      <Text>Customize Navigation</Text>
    </Button>
  );

  return (
    <>
      {isMobile ? (
        <Drawer onOpenChange={setOpen} open={open}>
          <DrawerTrigger render={tile} />
          {/* Min height instead of hugging content, so it lands at the same
            * size as the other navigation drawers. */}
          <DrawerPopup className="min-h-[50dvh]" showBar>
            <DrawerHeader>
              <DrawerTitle>More modules</DrawerTitle>
            </DrawerHeader>
            <DrawerPanel>{rows}</DrawerPanel>
            <DrawerFooter variant="bare">{customizeButton}</DrawerFooter>
          </DrawerPopup>
        </Drawer>
      ) : (
        <Popover onOpenChange={setOpen} open={open}>
          <PopoverTrigger render={tile} />
          <PopoverPopup
            align="start"
            // Keep the popup's top at or below the rail's top edge on short
            // screens.
            collisionPadding={{ top: popoverTopInset }}
            side="right"
            sideOffset={8}
          >
            <Box direction="column" gap="sm">
              <Text size="sm" weight="bold">
                More modules
              </Text>
              {/* The row list scrolls on short viewports; the title above and
                * the Customize action below stay pinned. The capped flex
                * column is what gives ScrollArea a definite height to scroll
                * within. */}
              <Box direction="column" maxHeight="50dvh">
                <ScrollArea>{rows}</ScrollArea>
              </Box>
              <Separator />
              {customizeButton}
            </Box>
          </PopoverPopup>
        </Popover>
      )}
      <CustomizeNavigationDialog
        initialTab="navigation"
        onOpenChange={setCustomizeOpen}
        open={customizeOpen}
      />
    </>
  );
}
